import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Globe2, Cpu, Database, Cloud, Mail } from "lucide-react";

const content = {
    hero: {
        eyebrow: "CASE STUDY",
        title: "AI‑Powered Medical Translation System",
        subtitle:
            "Bringing evidence‑based health information to Spanish‑speaking communities",
        client: "MedExplain",
        role: "Lead Developer & Solutions Architect",
        timeline: "2025",
        technologies: [
            "Azure AI Translator",
            "React",
            "Node.js",
            "Cosmos DB",
        ],
    },
    overview: {
        challenge: {
            title: "The Challenge",
            bullets: [
                "Manual translation of medical articles was slow (hours per article)",
                "Expensive ($0.20–0.40/word for professional medical translators)",
                "Inconsistent terminology across translators",
                "Bottleneck limiting content velocity for 500M+ Spanish speakers",
                "Need to preserve clinical accuracy and HTML structure",
            ],
        },
        solution: {
            title: "The Solution",
            lead:
                "An intelligent, one‑click translation system that automates the entire Spanish localization workflow.",
            core: [
                "Full article translation (title, body, keywords, quiz questions)",
                "HTML‑aware translation that preserves formatting & structure",
                "Editorial draft → review → publish workflow",
            ],
        },
    },
    challengesSolved: [
        {
            k: "Azure API Limits",
            problem:
                "Azure Translator has a 50,000‑character limit per request.",
            solution: [
                "Smart chunking algorithm with a 40K safety buffer",
                "Order preservation across multi‑call batches",
                "Edge cases for very large single strings",
            ],
        },
        {
            k: "HTML Preservation",
            problem:
                "Translate content while preserving HTML structure and attributes.",
            solution: [
                "Use Azure 'textType=html' + separate plain‑text vs HTML pathways",
                "Validate output structure equals input structure",
            ],
        },
        {
            k: "Medical Terminology Accuracy",
            problem:
                "Clinical terms must be translated consistently and correctly.",
            solution: [
                "Azure Translator medical domain + curated keyword handling",
                "Quoted‑phrase preservation; editorial QA workflow",
            ],
        },
        {
            k: "Quiz Translation Logic",
            problem: "Multiple‑choice quizzes have nested structure.",
            solution: [
                "Flatten to a deterministic array before translation",
                "Translate in order: question → options → explanations",
                "Reconstruct + keep correctness markers and \"I don't know\" sentinel",
            ],
        },
    ],
    results: {
        impact: [
            { label: "99% faster translation", sub: "Hours → Seconds" },
            { label: "90% cost reduction", sub: "Eliminated per‑word fees" },
            { label: "100% terminology consistency", sub: "Uniform medical language" },
            { label: "500M+ potential reach", sub: "Spanish‑speaking audiences" },
        ],
        metrics: [
            { name: "Avg time / article", value: "< 5 sec" },
            { name: "Article size handled", value: "3,000+ words" },
            { name: "Clinical accuracy", value: "Maintained" },
            { name: "Reliability", value: "Zero‑downtime Azure deploys" },
        ],
        businessValue: [
            "Eliminated translation bottleneck for editors",
            "Enabled same‑day Spanish publishing",
            "Maintained editorial control with draft/publish workflow",
            "Positioned platform for additional languages",
        ],
    },
    tech: {
        ai: ["Azure AI Translator API", "NLP helpers"],
        frontend: [
            "React 18",
            "TinyMCE Rich Text Editor",
            "React Select (AsyncSelect)",
            "DOMPurify",
        ],
        backend: ["Node.js", "Express.js", "Axios", "Luxon"],
        database: ["Azure Cosmos DB", "NoSQL document model", "Cross‑partition queries"],
        cloud: ["Azure App Services", "Azure AI Services", "Azure Cosmos DB"],
    },
    takeaways: [
        "AI Integration: Practical Azure AI to solve real business problems",
        "Healthcare Domain: Respect for clinical terminology & workflows",
        "Full‑Stack Delivery: UI → API → DB, end‑to‑end",
        "Cloud Architecture: Scalable, production‑ready on Azure",
        "UX: One‑click simplicity hiding complex backend logic",
    ],
    cta: {
        header: "Interested in Similar Solutions?",
        lead:
            "I build AI‑powered automation that removes bottlenecks and scales content ops.",
        bullets: [
            "Content translation & localization",
            "Document processing automation",
            "Azure/OpenAI service integration",
            "Healthcare / medical software",
            "Full‑stack web apps",
        ],
        email: "sanspaugh@zumosoft.com",
    },
};

const fadeUp = {
    initial: { opacity: 0, y: 12 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.2 },
    transition: { duration: 0.5 },
};

export default function MedExplainCaseStudy({ heroImage = "", uiShots = [], uiCaptions = [] }) {
    // Lightbox state for screenshots
    const [open, setOpen] = useState(false);
    const [idx, setIdx] = useState(0);
    const hasShots = Array.isArray(uiShots) && uiShots.length > 0;

    const close = () => setOpen(false);
    const next = () => setIdx((i) => (i + 1) % uiShots.length);
    const prev = () => setIdx((i) => (i - 1 + uiShots.length) % uiShots.length);
    const openAt = (i) => { setIdx(i); setOpen(true); };

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") close();
            if (e.key === "ArrowRight") next();
            if (e.key === "ArrowLeft") prev();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return (
        <div className="min-h-screen bg-neutral-50 text-neutral-900">
            {/* BACK LINK */}
            {/*<div className="mx-auto max-w-7xl px-6 pt-6">*/}
            {/*    <a*/}
            {/*        href="/case-studies"*/}
            {/*        className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900 transition-colors"*/}
            {/*    >*/}
            {/*        ← Back to Case Studies*/}
            {/*    </a>*/}
            {/*</div>*/}
            {/* HERO */}
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-emerald-50" />
                <div className="relative mx-auto max-w-7xl px-6 py-20 md:py-28">
                    <motion.div {...fadeUp} className="max-w-3xl">
                        <div className="mb-4 text-sm font-semibold tracking-widest text-indigo-600">
                            {content.hero.eyebrow}
                        </div>
                        <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl">
                            {content.hero.title}
                        </h1>
                        <p className="mt-4 text-lg text-neutral-700">
                            {content.hero.subtitle}
                        </p>
                        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-neutral-600">
                            <div><span className="font-medium text-neutral-900">Client:</span> {content.hero.client}</div>
                            <div><span className="font-medium text-neutral-900">Role:</span> {content.hero.role}</div>
                            <div><span className="font-medium text-neutral-900">Timeline:</span> {content.hero.timeline}</div>
                            <div className="w-full sm:w-auto">
                                <span className="font-medium text-neutral-900">Technologies:</span> {content.hero.technologies.join(", ")}
                            </div>
                        </div>
                    </motion.div>

                    {heroImage ? (
                        <motion.div
                            {...fadeUp}
                            className="mt-10 overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm"
                        >
                            <img src={heroImage} alt="MedExplain UI" className="w-full" />
                        </motion.div>
                    ) : null}
                </div>
            </section>

            {/* OVERVIEW */}
            <section className="mx-auto max-w-7xl grid grid-cols-1 gap-10 px-6 py-16 md:grid-cols-2">
                <motion.div {...fadeUp}>
                    <h2 className="text-2xl font-bold">{content.overview.challenge.title}</h2>
                    <ul className="mt-4 space-y-2 text-neutral-700 list-disc pl-5">
                        {content.overview.challenge.bullets.map((b) => (
                            <li key={b}>{b}</li>
                        ))}
                    </ul>
                </motion.div>
                <motion.div {...fadeUp}>
                    <h2 className="text-2xl font-bold">{content.overview.solution.title}</h2>
                    <p className="mt-2 text-neutral-700">{content.overview.solution.lead}</p>
                    <ul className="mt-4 space-y-2 text-neutral-700 list-disc pl-5">
                        {content.overview.solution.core.map((b) => (
                            <li key={b}>{b}</li>
                        ))}
                    </ul>
                </motion.div>
            </section>

            {/* UI SHOTS */}
            {hasShots ? (
                <section className="mx-auto max-w-7xl px-6 pb-8">
                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {uiShots.map((src, i) => (
                            <motion.div
                                key={src + i}
                                {...fadeUp}
                                className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm cursor-pointer hover:shadow-lg transition-shadow"
                            >
                                <button
                                    onClick={() => openAt(i)}
                                    className="group block w-full text-left focus:outline-none"
                                    aria-label={`Open screenshot ${i + 1}`}
                                >
                                    <img src={src} alt={uiCaptions[i] || `Screenshot ${i + 1}`} className="w-full" loading="lazy" />
                                    <div className="flex items-center justify-between px-4 py-3">
                                        <span className="text-sm text-neutral-600">{uiCaptions[i] || `Screenshot ${i + 1}`}</span>
                                        <span className="text-xs text-neutral-500 opacity-0 group-hover:opacity-100 transition-opacity">Click to enlarge</span>
                                    </div>
                                </button>
                            </motion.div>
                        ))}
                    </div>

                    {/* Lightbox modal */}
                    {open && (
                        <div
                            role="dialog"
                            aria-modal="true"
                            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
                            onClick={close}
                        >
                            <div
                                className="relative w-full max-w-6xl"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <img
                                    src={uiShots[idx]}
                                    alt={uiCaptions[idx] || `Screenshot ${idx + 1}`}
                                    className="w-full rounded-2xl shadow-2xl"
                                />
                                <div className="mt-3 px-2 text-center text-white text-lg font-medium">
                                    {uiCaptions[idx] || `Screenshot ${idx + 1}`}
                                </div>

                                {/* Controls */}
                                <button
                                    onClick={close}
                                    className="absolute right-4 top-4 rounded-full bg-white/95 px-4 py-2 text-sm font-semibold text-neutral-900 shadow-lg hover:bg-white transition-colors"
                                    aria-label="Close lightbox"
                                >
                                    Close ✕
                                </button>

                                {uiShots.length > 1 && (
                                    <>
                                        <button
                                            onClick={prev}
                                            className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/95 px-4 py-2 text-sm font-semibold text-neutral-900 shadow-lg hover:bg-white transition-colors"
                                            aria-label="Previous image"
                                        >
                                            ← Prev
                                        </button>
                                        <button
                                            onClick={next}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/95 px-4 py-2 text-sm font-semibold text-neutral-900 shadow-lg hover:bg-white transition-colors"
                                            aria-label="Next image"
                                        >
                                            Next →
                                        </button>
                                    </>
                                )}

                                {/* Image counter */}
                                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-4 py-1 text-sm text-white">
                                    {idx + 1} / {uiShots.length}
                                </div>
                            </div>
                        </div>
                    )}
                </section>
            ) : null}

            {/* KEY TECHNICAL CHALLENGES */}
            <section className="mx-auto max-w-7xl px-6 py-16">
                <motion.h2 {...fadeUp} className="text-3xl font-extrabold">
                    Key Technical Challenges Solved
                </motion.h2>
                <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
                    {content.challengesSolved.map((c) => (
                        <motion.div
                            key={c.k}
                            {...fadeUp}
                            className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm"
                        >
                            <h3 className="text-xl font-bold">{c.k}</h3>
                            <p className="mt-2 text-neutral-700"><span className="font-medium">Problem:</span> {c.problem}</p>
                            <div className="mt-3">
                                <div className="font-medium">Solution:</div>
                                <ul className="mt-2 list-disc space-y-1 pl-5 text-neutral-700">
                                    {c.solution.map((s) => (
                                        <li key={s}>{s}</li>
                                    ))}
                                </ul>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* RESULTS */}
            <section className="relative">
                <div className="absolute inset-0 -z-10 bg-gradient-to-br from-white via-indigo-50 to-emerald-50" />
                <div className="mx-auto max-w-7xl px-6 py-16">
                    <motion.h2 {...fadeUp} className="text-3xl font-extrabold">The Results</motion.h2>
                    <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
                        <motion.div {...fadeUp} className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
                            <h3 className="text-lg font-semibold">Impact</h3>
                            <ul className="mt-3 space-y-2">
                                {content.results.impact.map((i) => (
                                    <li key={i.label} className="flex items-start gap-2">
                                        <CheckCircle2 className="h-5 w-5 flex-none text-emerald-600" />
                                        <div>
                                            <div className="font-medium">{i.label}</div>
                                            <div className="text-sm text-neutral-600">{i.sub}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </motion.div>
                        <motion.div {...fadeUp} className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
                            <h3 className="text-lg font-semibold">Technical Metrics</h3>
                            <dl className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2">
                                {content.results.metrics.map((m) => (
                                    <div key={m.name} className="rounded-xl bg-neutral-50 p-4">
                                        <dt className="text-sm text-neutral-600">{m.name}</dt>
                                        <dd className="text-2xl font-bold text-indigo-600">{m.value}</dd>
                                    </div>
                                ))}
                            </dl>
                        </motion.div>
                    </div>
                    <motion.div {...fadeUp} className="mt-6 rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
                        <h3 className="text-lg font-semibold">Business Value</h3>
                        <ul className="mt-3 list-disc space-y-2 pl-5 text-neutral-700">
                            {content.results.businessValue.map((b) => (
                                <li key={b}>{b}</li>
                            ))}
                        </ul>
                    </motion.div>
                </div>
            </section>

            {/* TECH STACK */}
            <section className="mx-auto max-w-7xl px-6 py-16">
                <motion.h2 {...fadeUp} className="text-3xl font-extrabold">Technologies Used</motion.h2>
                <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                    <TechCard icon={<Cpu />} title="AI / ML" items={content.tech.ai} />
                    <TechCard icon={<Globe2 />} title="Frontend" items={content.tech.frontend} />
                    <TechCard icon={<Cloud />} title="Backend" items={content.tech.backend} />
                    <TechCard icon={<Database />} title="Database" items={content.tech.database} />
                    <TechCard icon={<Cloud />} title="Cloud Infrastructure" items={content.tech.cloud} />
                </div>
            </section>

            {/* TAKEAWAYS */}
            <section className="mx-auto max-w-7xl px-6 pb-8">
                <motion.h2 {...fadeUp} className="text-3xl font-extrabold">Key Takeaways</motion.h2>
                <ul className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                    {content.takeaways.map((t) => (
                        <motion.li
                            key={t}
                            {...fadeUp}
                            className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm"
                        >
                            {t}
                        </motion.li>
                    ))}
                </ul>
            </section>

            {/* CTA */}
            <section className="relative">
                <div className="absolute inset-0 -z-10 bg-gradient-to-r from-indigo-600 to-emerald-500 opacity-10" />
                <div className="mx-auto max-w-5xl px-6 py-14 text-center">
                    <motion.h2 {...fadeUp} className="text-2xl font-extrabold">
                        {content.cta.header}
                    </motion.h2>
                    <motion.p {...fadeUp} className="mx-auto mt-2 max-w-3xl text-neutral-700">
                        {content.cta.lead}
                    </motion.p>
                    <motion.ul {...fadeUp} className="mt-6 flex flex-wrap items-center justify-center gap-3 text-sm text-neutral-700">
                        {content.cta.bullets.map((b) => (
                            <li key={b} className="rounded-full border border-neutral-200 bg-white px-3 py-1 shadow-sm">{b}</li>
                        ))}
                    </motion.ul>
                    <motion.a
                        {...fadeUp}
                        href={`mailto:${content.cta.email}`}
                        className="mt-8 inline-flex items-center gap-2 rounded-2xl bg-neutral-900 px-5 py-3 font-semibold text-white shadow hover:bg-neutral-800 transition-colors"
                    >
                        <Mail className="h-5 w-5" /> Contact Zumosoft
                    </motion.a>
                </div>
            </section>
        </div>
    );
}

function TechCard({ icon, title, items }) {
    return (
        <motion.div
            {...fadeUp}
            className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm"
        >
            <div className="flex items-center gap-2">
                <span className="text-indigo-600">{icon}</span>
                <h3 className="text-lg font-semibold">{title}</h3>
            </div>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-neutral-700">
                {items.map((i) => (
                    <li key={i}>{i}</li>
                ))}
            </ul>
        </motion.div>
    );
}